import React, { useState, useEffect } from "react";
import Button from "../Button";
import SampleResults from "./SampleResults";
import SampleReports from "./SampleReports";
import SampleFiles from "./SampleFiles";
import ClientSpecificationView from "./ClientSpecificationView";
import SampleSummaryView from "./SampleSummaryView";
import SampleComments from "../registration/SampleComments";
import RequestedTesting from "../registration/RequestedTesting";
import SampleRetestSamples from "./SampleRetestSamples";
import SampleLedgerView from "../invoicing/SampleLedgerView";
import EditSamplePricing from "./EditSamplePricing";
import { takeSamplePhoto } from "../../lib/TakePhoto";

export default function SampleView({
  sample,
  clientView,
  initialView,
  onClose,
  reloadSample,
  reloadSamples,
}) {
  const [view, setView] = useState(false);

  const updateView = (newView) => {
    if (view === newView) setView(false);
    else setView(newView);
  };

  const sampleDetailButtons = [
    { title: "Summary", view: "summary", show: true },
    { title: I18n.t("requested_testing"), view: "requested_testing", show: true },
    {
      title: I18n.t("sample_retests"),
      view: "sample_retests",
      show: true && !sample.withdrawn,
    },
    {
      title: I18n.t("sample_pricing"),
      view: "sample_pricing",
      show: !clientView && !sample.withdrawn,
    },
  ];

  const spec = [
    {
      title: I18n.t("client_specification"),
      view: "client_specification",
      show: sample.client_specification_id || sample.ledger_item_id,
    },
  ];

  const resultButtons = [
    {
      title: I18n.t("exceeded_results"),
      view: "exceeded_results",
      show: sample.contract_reviewed || sample.ledger_item_id,
    },
    {
      title: I18n.t("detected_results"),
      view: "detected_results",
      show: sample.contract_reviewed || sample.ledger_item_id,
    },
    {
      title: I18n.t("available_results"),
      view: "available_results",
      show: sample.contract_reviewed || sample.ledger_item_id,
    },
    {
      title: I18n.t("all_results"),
      view: "all_results",
      show: sample.contract_reviewed || sample.ledger_item_id,
    },
    {
      title: I18n.t("laboratory_results"),
      view: "laboratory_results",
      admin: true,
      show: sample.contract_reviewed || sample.ledger_item_id,
    },
  ];

  const reportButtons = [
    {
      title: I18n.t("reports"),
      view: "reports",
      show: sample.contract_reviewed || sample.ledger_item_id,
    },
  ];

  const commentButtons = [
    { title: I18n.t("comments_and_todos"), icon: "fa-comment", view: "comments", show: true },
  ];

  const invoiceButtons = [
    { title: I18n.t("invoicing_history"), view: "invoicing_history", show: !clientView },
  ];

  const fileButtons = [
    {
      title: "Client Files",
      view: "sample_files",
      show: true,
    },
  ];

  const buttonGroups =
    clientView && sample.withdrawn
      ? [sampleDetailButtons]
      : [
          sampleDetailButtons,
          spec,
          resultButtons,
          reportButtons,
          fileButtons,
          commentButtons,
          invoiceButtons,
        ];

  useEffect(
    () => {
      setView(initialView);
    },
    [initialView]
  );

  return (
    <div>
      <div style={{ marginBottom: view ? 5 : 0 }}>
        <div className="btn-group pull-right">
          <Button title="X" onClick={onClose} tiny deleting />
        </div>
        {buttonGroups.map(
          (buttons, index) =>
            buttons.filter((b) => b.show).length > 0 && (
              <div key={index} className="btn-group" style={{ marginRight: 10 }}>
                {buttons.map(
                  (button) =>
                    button.show &&
                    (!button.admin || !clientView) && (
                      <Button
                        key={button.title}
                        title={button.title}
                        onClick={() => updateView(button.view)}
                        highlight={view === button.view}
                        icon={button.icon}
                        tiny
                      />
                    )
                )}
              </div>
            )
        )}
        {!clientView && (
          <div key="take-sample-photo" className="btn-group" style={{ marginRight: 10 }}>
            <Button
              title={false}
              icon="fa-camera"
              onClick={() => takeSamplePhoto(sample.identifier, "Sample " + sample.identifier)}
              tiny
            />
          </div>
        )}
      </div>

      {view == "summary" && <SampleSummaryView id={sample.identifier} clientView={clientView} />}

      {view === "sample_retests" && (
        <SampleRetestSamples sampleId={sample.identifier} clientView={clientView} />
      )}

      {view === "sample_pricing" && (
        <EditSamplePricing
          sampleId={sample.identifier}
          onClose={onClose}
          onChange={reloadSamples}
        />
      )}

      {view === "requested_testing" && (
        <div style={{ backgroundColor: "white", paddingTop: 1, paddingBottom: 1 }}>
          <RequestedTesting
            onChange={reloadSamples}
            sampleId={sample.identifier}
            companyId={sample.company_id}
            clientSpecificationId={sample.client_specification_id}
            onClose={onClose}
            displayOnly={
              sample.withdrawn ||
              (clientView && (sample.received_at || sample.contract_reviewed))
            }
          />
        </div>
      )}
      {view == "client_specification" && (
        <ClientSpecificationView
          id={sample.client_specification_id}
          name={sample.client_specification_name}
          at={sample.registered_at}
        />
      )}
      {[
        "exceeded_results",
        "detected_results",
        "available_results",
        "all_results",
        "laboratory_results",
      ].includes(view) && (
        <SampleResults
          key={view}
          sample={sample}
          onClose={onClose}
          clientView={clientView}
          searchType={view}
        />
      )}
      {view == "reports" && <SampleReports sample={sample} onClose={onClose} />}
      {view == "comments" && (
        <SampleComments
          onChange={reloadSample}
          sample_id={sample.identifier}
          onClose={onClose}
          clientView={clientView}
        />
      )}
      {view == "invoicing_history" && <SampleLedgerView sample_id={sample.identifier} />}
      {view == "sample_files" && <SampleFiles sample={sample} />}
    </div>
  );
}
